import { useCallback } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/context/providers/AuthProvider";
import type { GalleryDress } from "@/types/gallery";
import { logger, toError } from "@/utils/logger";

/**
 * Hook for gallery actions (Use Design functionality)
 * Separated from data fetching for cleaner architecture
 */
export function useGalleryActions() {
  const router = useRouter();
  const { user } = useAuth();

  /**
   * Handle "Use Design" action
   * For completed tickets, duplicates the ticket
   * Otherwise, redirects to create page with design data
   */
  const handleUseDesign = useCallback(
    async (
      dress: GalleryDress,
      locationData?: {
        zipCode: string;
        city: string;
        state: string;
        country: string;
      }
    ) => {
      if (!user) {
        const designData = {
          id: dress.id, // Include ticket ID so we can duplicate after login
          prompt: dress.prompt || "",
          image_url: dress.image_url || "",
          original_image_path:
            dress.original_image_path || dress.image_url || "",
          ...(locationData && {
            city: locationData.city,
            state: locationData.state,
            zip_code: locationData.zipCode,
            country: locationData.country,
          }),
        };
        const target = `/create?gallery_design=${encodeURIComponent(JSON.stringify(designData))}`;
        router.push(`/login?redirect=${encodeURIComponent(target)}`);
        return;
      }

      try {
        // Try to duplicate the ticket if it's a completed ticket
        const response = await fetch("/api/gallery/duplicate", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            originalTicketId: dress.id,
            ...(locationData && {
              city: locationData.city,
              state: locationData.state,
              zip_code: locationData.zipCode,
              country: locationData.country,
            }),
          }),
        });

        if (response.ok) {
          const data = await response.json();
          // Redirect to the new ticket
          router.push(`/ticket/${data.ticket.id}`);
        } else {
          // If duplication fails (e.g., not a completed ticket), use create page approach
          const designData = {
            id: dress.id, // Include ID in case we want to retry duplication
            prompt: dress.prompt || "",
            image_url: dress.image_url || "",
            original_image_path:
              dress.original_image_path || dress.image_url || "",
            ...(locationData && {
              city: locationData.city,
              state: locationData.state,
              zip_code: locationData.zipCode,
              country: locationData.country,
            }),
          };
          router.push(
            `/create?gallery_design=${encodeURIComponent(JSON.stringify(designData))}`
          );
        }
      } catch (err) {
        logger.error(
          "Error using design",
          { component: "useGalleryActions", dressId: dress.id },
          toError(err)
        );
        // Fallback to create page approach
        const designData = {
          id: dress.id, // Include ID in case we want to retry duplication
          prompt: dress.prompt || "",
          image_url: dress.image_url || "",
          original_image_path:
            dress.original_image_path || dress.image_url || "",
          ...(locationData && {
            city: locationData.city,
            state: locationData.state,
            zip_code: locationData.zipCode,
            country: locationData.country,
          }),
        };
        router.push(
          `/create?gallery_design=${encodeURIComponent(JSON.stringify(designData))}`
        );
      }
    },
    [user, router]
  );

  return {
    handleUseDesign,
  };
}
